import React, { useState, useEffect } from 'react';
import { Sparkles, X, AlertTriangle, Loader2, ShieldCheck } from 'lucide-react';
import { InventoryItem } from '../types/inventory';
import { ConceptRoot } from '../types/catalog';
import { getConcepts, upsertCatalogItem } from '../services/catalogService';

export interface QuickAddCatalogItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onItemCreated: (newItem: InventoryItem) => void;
  conceptsList?: ConceptRoot[];
}

export const QuickAddCatalogItemModal: React.FC<QuickAddCatalogItemModalProps> = ({
  isOpen,
  onClose,
  onItemCreated,
  conceptsList: initialConceptsList
}) => {
  const [concepts, setConcepts] = useState<ConceptRoot[]>(initialConceptsList || []);
  const [quickConceptId, setQuickConceptId] = useState('');
  const [quickName, setQuickName] = useState('');
  const [quickCode, setQuickCode] = useState('');
  const [quickCategory, setQuickCategory] = useState('Herrajes y Tornillería');
  const [quickUom, setQuickUom] = useState('UND');
  const [quickCostUSD, setQuickCostUSD] = useState('1.00');
  const [isSavingQuick, setIsSavingQuick] = useState(false);
  const [quickError, setQuickError] = useState<string | null>(null);

  // Carga reactiva de conceptos raíz si no fueron inyectados previamente
  useEffect(() => {
    if (isOpen && concepts.length === 0) {
      getConcepts(false)
        .then(c => {
          if (c && c.length > 0) setConcepts(c);
        })
        .catch(err => {
          console.warn('[QuickAddCatalogItemModal] Error cargando conceptos ISO:', err);
        });
    }
  }, [isOpen, concepts.length]);

  // Manejador del cambio de concepto raíz para auto-completar prefijo y UoM
  const handleQuickConceptChange = (conceptId: string) => {
    setQuickConceptId(conceptId);
    const found = concepts.find(c => c.id === conceptId);
    if (found) {
      if (found.codePrefix && !quickCode) {
        setQuickCode(`${found.codePrefix}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`);
      }
      if (found.defaultUoM) {
        setQuickUom(found.defaultUoM.toUpperCase().includes('METRO') ? 'ML' : 'UND');
      }
    }
  };

  const handleSaveQuickAdd = async () => {
    if (!quickName.trim()) {
      setQuickError('El nombre del insumo es obligatorio.');
      return;
    }
    if (!quickCode.trim()) {
      setQuickError('El código (SKU) es obligatorio.');
      return;
    }

    setIsSavingQuick(true);
    setQuickError(null);
    try {
      const res = await upsertCatalogItem({
        action: 'create',
        nombre: quickName.trim(),
        codigo: quickCode.trim().toUpperCase(),
        categoria: quickCategory,
        conceptoId: quickConceptId || undefined,
        unidad: quickUom,
        costoUnitarioUSD: parseFloat(quickCostUSD) || 1.0,
        stockMinimo: 0,
        activo: true
      });

      if (!res.ok) {
        throw new Error(res.error || 'Error al registrar insumo en Notion');
      }

      const costVal = parseFloat(quickCostUSD) || 1.0;
      const newInvItem: InventoryItem = {
        id: res.dashboardId || res.insumoId || quickCode.trim().toUpperCase(),
        insumoId: res.insumoId,
        nombre: quickName.trim(),
        codigo: quickCode.trim().toUpperCase(),
        stockBase: 0,
        stockMinimo: 0,
        deficit: 1,
        estadoStock: 'Sin Stock',
        prioridad: 'Alta',
        categoriaMaterial: quickCategory,
        unidad: quickUom,
        costoUnitarioUSD: costVal
      };

      onItemCreated(newInvItem);
      onClose();
      // Resetear campos
      setQuickName('');
      setQuickCode('');
      setQuickCostUSD('1.00');
      setQuickConceptId('');
    } catch (e: any) {
      setQuickError(e.message || 'Error al guardar insumo');
    } finally {
      setIsSavingQuick(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fade-in no-print">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-lg w-full shadow-2xl text-slate-100 space-y-4">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
          <div className="flex items-center space-x-2 text-blue-400">
            <Sparkles className="w-5 h-5" />
            <h3 className="font-bold text-base text-white">Alta Rápida de Insumo al Vuelo</h3>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition"
            aria-label="Cerrar modal de alta rápida"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-xs text-slate-400">
          Registra el nuevo insumo en Notion ERP con Alta Dual Atómica y lo agrega inmediatamente a la lista de compra.
        </p>

        {quickError && (
          <div className="p-3 bg-rose-500/10 border border-rose-500/30 rounded-lg text-rose-300 text-xs flex items-center space-x-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>{quickError}</span>
          </div>
        )}

        <div className="space-y-3 text-xs">
          <div>
            <label htmlFor="oab_quick_concept" className="block text-slate-300 font-medium mb-1">
              Concepto Raíz ISO (Opcional)
            </label>
            <select
              id="oab_quick_concept"
              name="oab_quick_concept"
              value={quickConceptId}
              onChange={e => handleQuickConceptChange(e.target.value)}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:border-blue-500"
            >
              <option value="">-- Sin Concepto ISO --</option>
              {concepts.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.codePrefix})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="oab_quick_name" className="block text-slate-300 font-medium mb-1">
              Nombre del Insumo *
            </label>
            <input
              id="oab_quick_name"
              name="oab_quick_name"
              type="text"
              value={quickName}
              onChange={e => setQuickName(e.target.value)}
              placeholder="Ej: TORNILLO GYPSUM 6X1..."
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 font-semibold focus:outline-none focus:border-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="oab_quick_code" className="block text-slate-300 font-medium mb-1">
                Código (SKU) *
              </label>
              <input
                id="oab_quick_code"
                name="oab_quick_code"
                type="text"
                value={quickCode}
                onChange={e => setQuickCode(e.target.value.toUpperCase())}
                placeholder="Ej: TOR-9901"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg font-mono text-blue-300 uppercase focus:outline-none focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="oab_quick_uom" className="block text-slate-300 font-medium mb-1">
                Unidad de Medida
              </label>
              <select
                id="oab_quick_uom"
                name="oab_quick_uom"
                value={quickUom}
                onChange={e => setQuickUom(e.target.value)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:border-blue-500"
              >
                <option value="UND">Unidad (UND)</option>
                <option value="ML">Metro Lineal (ML)</option>
                <option value="PLANCHA">Plancha / Lámina</option>
                <option value="KG">Kilogramo (KG)</option>
                <option value="L">Litro (L)</option>
                <option value="PAR">Par (PAR)</option>
                <option value="CJ">Caja (CJ)</option>
                <option value="PAQ">Paquete (PAQ)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="oab_quick_category" className="block text-slate-300 font-medium mb-1">
                Categoría
              </label>
              <select
                id="oab_quick_category"
                name="oab_quick_category"
                value={quickCategory}
                onChange={e => setQuickCategory(e.target.value)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:border-blue-500"
              >
                <option value="Herrajes y Accesorios">Herrajes y Accesorios</option>
                <option value="Adhesivos y Químicos">Adhesivos y Químicos</option>
                <option value="Tableros">Tableros</option>
                <option value="Fijaciones (Tornillería)">Fijaciones (Tornillería)</option>
                <option value="Laminados Decorativos">Laminados Decorativos</option>
                <option value="Tapacantos">Tapacantos</option>
                <option value="Tubería (Redonda)">Tubería (Redonda)</option>
                <option value="Láminas">Láminas</option>
                <option value="Platinas">Platinas</option>
                <option value="Mallas">Mallas</option>
                <option value="Tubería (Cuadrada/Rectangular)">Tubería (Cuadrada/Rectangular)</option>
                <option value="Barras y Varillas">Barras y Varillas</option>
                <option value="Tubería (Especializada)">Tubería (Especializada)</option>
                <option value="Herramientas y Abrasivos">Herramientas y Abrasivos</option>
                <option value="Suministros Neumáticos">Suministros Neumáticos</option>
                <option value="Equipo de Protección Personal (EPP)">Equipo de Protección Personal (EPP)</option>
                <option value="Indumentaria y Seguridad">Indumentaria y Seguridad</option>
                <option value="Equipos y Maquinaria">Equipos y Maquinaria</option>
                <option value="Suministros de Limpieza">Suministros de Limpieza</option>
                <option value="Perfiles y Tubos">Perfiles y Tubos</option>
                <option value="Abrasivos">Abrasivos</option>
                <option value="Maderas y Tableros">Maderas y Tableros</option>
                <option value="Herrajes y Tornillería">Herrajes y Tornillería</option>
                <option value="Químicos y Pegamentos">Químicos y Pegamentos</option>
                <option value="Vidrios y Espejos">Vidrios y Espejos</option>
                <option value="Metales y Perfilería">Metales y Perfilería</option>
                <option value="Empaque y Embalaje">Empaque y Embalaje</option>
                <option value="General">General</option>
              </select>
            </div>

            <div>
              <label htmlFor="oab_quick_cost" className="block text-slate-300 font-medium mb-1">
                Costo Base ($ USD)
              </label>
              <input
                id="oab_quick_cost"
                name="oab_quick_cost"
                type="number"
                step="0.01"
                min="0"
                value={quickCostUSD}
                onKeyDown={(e) => {
                  if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
                }}
                onChange={e => setQuickCostUSD(e.target.value)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg font-mono text-emerald-400 font-bold focus:outline-none focus:border-emerald-500"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end space-x-2 pt-3 border-t border-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold rounded-lg transition"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSaveQuickAdd}
            disabled={isSavingQuick}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-800 text-white text-xs font-bold rounded-lg flex items-center space-x-1.5 shadow transition active:scale-95"
          >
            {isSavingQuick ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Guardando...</span>
              </>
            ) : (
              <>
                <ShieldCheck className="w-3.5 h-3.5" />
                <span>Crear e Insertar en OAB</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
